import { UseFormReturn, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { useListZones } from 'src/features/zone/api/useListZones';
import { SystemComponentFormSchemaValues } from 'src/features/systems/utils/formSchema';
import {
    CAMERA_TYPE,
    COMMUNICATOR_TYPE,
    LISTEN_STATE,
    SYSTEM_COMPONENT,
    SYSTEM_COMPONENT_TYPE,
} from 'src/features/systems/utils/types';
import {
    COMPONENT_DATA_TYPE_TRANSLATIONS_MAP,
    LISTEN_STATE_TRANSLATIONS_MAP,
    ListOfBanknoteCoinComponents,
    SYSTEM_COMPONENT_TRANSLATIONS_MAP,
    SYSTEM_COMPONENT_TYPE_TRANSLATIONS_MAP,
} from 'src/features/systems/utils/constants';
import { systemsMessages } from 'src/features/systems/utils/messages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import GatewayDisplayFormFields from 'src/features/systems/components/systemComponents/form/GatewayDisplayFormFields';
import BanknoteCoinComponentsFormFields from 'src/features/systems/components/systemComponents/form/BanknoteCoinComponentsFormFields';

type SystemComponentFormProps = {
    form: UseFormReturn<SystemComponentFormSchemaValues>;
    onSubmit: (values: SystemComponentFormSchemaValues) => void;
};
function SystemComponentForm({ onSubmit, form }: SystemComponentFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: zoneData } = useListZones({ parkingId });
    const { data: gatewayData } = useListGateways({ parkingId });

    const { component } = form.watch();
    const dataType = useWatch({ control: form.control, name: 'data.type' });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<SystemComponentFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <SelectFormField<SystemComponentFormSchemaValues>
                options={Object.values(SYSTEM_COMPONENT_TYPE)}
                form={form}
                name="component_type"
                label={formatMessage(systemsMessages.componentType)}
                optionTranslationMap={SYSTEM_COMPONENT_TYPE_TRANSLATIONS_MAP}
            />
            <SelectFormField<SystemComponentFormSchemaValues>
                onChange={() => form.setValue('data.type', '')}
                options={Object.values(SYSTEM_COMPONENT)}
                form={form}
                name="component"
                label={formatMessage(systemsMessages.component)}
                optionTranslationMap={SYSTEM_COMPONENT_TRANSLATIONS_MAP}
            />
            <InputFormField<SystemComponentFormSchemaValues>
                form={form}
                name="code"
                label={formatMessage(systemsMessages.code)}
            />
            <FormField
                control={form.control}
                name="zone"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...systemsMessages.zone} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(systemsMessages.zone)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {zoneData?.results.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.translations.find((option) => option.language === locale)?.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="gateway"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage
                                id={systemsMessages.gateway.id}
                                defaultMessage={systemsMessages.gateway.defaultMessage}
                            />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(systemsMessages.gateway)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {gatewayData?.results.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.translations.find((option) => option.language === locale)?.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {component && component === SYSTEM_COMPONENT.GATEWAY_DISPLAY && (
                <GatewayDisplayFormFields form={form} />
            )}

            {component &&
                !ListOfBanknoteCoinComponents.includes(component) &&
                component !== SYSTEM_COMPONENT.GATEWAY_DISPLAY && (
                    <>
                        <SelectFormField<SystemComponentFormSchemaValues>
                            options={Object.values(
                                component === SYSTEM_COMPONENT.LPR_CAMERA ? CAMERA_TYPE : COMMUNICATOR_TYPE,
                            )}
                            form={form}
                            name="data.type"
                            label={formatMessage(systemsMessages.type)}
                            optionTranslationMap={COMPONENT_DATA_TYPE_TRANSLATIONS_MAP}
                        />
                        <InputFormField<SystemComponentFormSchemaValues>
                            form={form}
                            name="data.make"
                            label={formatMessage(systemsMessages.make)}
                        />
                        <InputFormField<SystemComponentFormSchemaValues>
                            form={form}
                            name="data.model"
                            label={formatMessage(systemsMessages.model)}
                        />
                        <InputFormField<SystemComponentFormSchemaValues>
                            form={form}
                            name="data.serial_number"
                            label={formatMessage(systemsMessages.serialNumber)}
                        />
                        {component === SYSTEM_COMPONENT.LPR_CAMERA ? (
                            <InputFormField<SystemComponentFormSchemaValues>
                                form={form}
                                name="data.rtsp_stream_url"
                                label={formatMessage(systemsMessages.RTSPStreamUrl)}
                            />
                        ) : (
                            <>
                                <InputFormField<SystemComponentFormSchemaValues>
                                    form={form}
                                    name="data.channel"
                                    type="number"
                                    label={formatMessage(systemsMessages.channel)}
                                />
                                <InputFormField<SystemComponentFormSchemaValues>
                                    form={form}
                                    name="data.topic_code"
                                    label={formatMessage(systemsMessages.topicCode)}
                                />
                                {component === SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR && (
                                    <>
                                        <InputFormField<SystemComponentFormSchemaValues>
                                            form={form}
                                            name="data.persistent_channel"
                                            type="number"
                                            label={formatMessage(systemsMessages.persistentChannel)}
                                        />
                                        <InputFormField<SystemComponentFormSchemaValues>
                                            form={form}
                                            name="data.open_duration"
                                            type="number"
                                            label={formatMessage(systemsMessages.pulseDuration)}
                                        />
                                        {dataType === COMMUNICATOR_TYPE.SP_MQTT && (
                                            <>
                                                <InputFormField<SystemComponentFormSchemaValues>
                                                    form={form}
                                                    name="data.status_channel"
                                                    type="number"
                                                    label={formatMessage(systemsMessages.statusChannel)}
                                                />
                                                <SelectFormField<SystemComponentFormSchemaValues>
                                                    options={Object.values(LISTEN_STATE)}
                                                    form={form}
                                                    name="data.status_listen_state"
                                                    label={formatMessage(systemsMessages.statusListenState)}
                                                    optionTranslationMap={LISTEN_STATE_TRANSLATIONS_MAP}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                                {component === SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR &&
                                    dataType === COMMUNICATOR_TYPE.SP_MQTT && (
                                        <SelectFormField<SystemComponentFormSchemaValues>
                                            options={Object.values(LISTEN_STATE)}
                                            form={form}
                                            name="data.listen_state"
                                            label={formatMessage(systemsMessages.listenState)}
                                            optionTranslationMap={LISTEN_STATE_TRANSLATIONS_MAP}
                                        />
                                    )}
                            </>
                        )}
                    </>
                )}

            {component && ListOfBanknoteCoinComponents.includes(component) && (
                <BanknoteCoinComponentsFormFields form={form} />
            )}
        </Form>
    );
}

export default SystemComponentForm;
